// Every text the courier sees. English first (D-0017); Urdu and Hindi files are added later
// with the same keys, and Urdu sets right-to-left (D-0013).
export const en = {
  dir: 'ltr' as 'ltr' | 'rtl',
  app: {
    name: 'Blink Driver',
    tagline: 'Your deliveries for today',
  },
  login: {
    title: 'Sign in',
    subtitle: 'Use the username and password your team leader gave you.',
    identifier: 'Username (mobile number or email)',
    password: 'Password',
    submit: 'Sign in',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    expired: 'You were signed out. Please sign in again.',
    signedOut: 'You are signed out.',
    forgot: 'Forgot your password? Ask your team leader to reset it.',
    lockedUntil: 'Too many wrong passwords. Try again after {time}.',
  },
  changePassword: {
    title: 'Choose your own password',
    subtitle: 'This is your first sign-in. Replace the temporary password with your own.',
    current: 'Temporary password',
    new: 'New password',
    confirm: 'New password again',
    rules: 'At least {min} characters. A short sentence is easy to remember, e.g. "blue van to deira 2026".',
    mismatch: 'The two new passwords are not the same.',
    submit: 'Save password',
  },
  home: {
    hello: 'Hello, {name}',
    today: 'Today',
    team: 'Team',
    hub: 'Hub',
    vehicle: 'Vehicle',
    parcels: 'Parcels',
    pieces: 'Pieces: {count}',
    cod: 'Cash to collect',
    toLoad: 'To load',
    onRoute: 'On the route',
    done: 'Done today',
    doneHint: '{delivered} delivered · {failed} not delivered',
    next: 'Next stop',
    noNext: 'Load your parcels and start the route to see the next stop.',
    loadHint: 'Scan them into the vehicle',
    scanLoad: 'Scan & load ({count} to load)',
    startLoaded: 'Loaded – start route ({count} ready)',
    started: 'Route started. Out for delivery: {out}.',
    startedLeft: 'Not fully loaded, staying with the dispatcher: {left}.',
    allParcels: 'Your parcels',
    noDistrict: 'District not known',
    emptyTitle: 'No parcels for you yet',
    emptyText: 'When the dispatcher gives you parcels they appear here at once.',
  },
  route: {
    title: 'Route',
    summary: '{km} km · finish about {time}',
    fromHub: 'from the hub',
    fromHere: 'from where you were',
    straight: 'Test order (straight lines)',
    reorder: 'Re-order from here',
    reordering: 'Working out…',
    outdated: 'New parcels on the route: re-order to fit them in.',
    noLocation: 'Location not available: ordered from the hub.',
    navWith: 'Navigate with',
    stop: 'Stop {n}',
    eta: 'about {time}',
    go: 'Go',
    goHint: 'Marks this stop as "Going there now".',
    openNav: 'Open {app}',
    approximate: 'No exact pin: this takes you to the district. Use the address and call the customer.',
    noPin: 'No pin for this stop: use the address and call the customer.',
  },
  navApps: { google: 'Google Maps', waze: 'Waze', '2gis': '2GIS' },
  deliver: {
    scanTitle: 'Scan every piece',
    scanProgress: '{scanned} of {total} pieces scanned',
    answer: {
      SCANNED: 'Scanned',
      ALREADY_SCANNED: 'Already scanned',
      OTHER_PARCEL: 'This piece belongs to another parcel. Check you have the right one.',
      SCAN_PIECE_LABELS: 'This parcel has several pieces: scan each piece label (1/3, 2/3…).',
      UNKNOWN: 'Label not known. Check you scanned the Blink or client label.',
    },
    photoTitle: 'Proof photo',
    podKinds: { POD_EMIRATES_ID: 'Emirates ID', POD_VILLA: 'Villa number', POD_DOOR: 'Apartment door' },
    photoHint: 'Show the number and the parcel together in one photo.',
    photoHintId: 'Show the Emirates ID and the parcel together. Only managers and auditors can see this photo.',
    takePhoto: 'Take photo',
    retake: 'Take again',
    photoSaving: 'Saving photo…',
    photoSaved: 'Photo saved: {kind}, {time}',
    paymentTitle: 'Collect {amount}',
    methods: { CASH: 'Cash', PAYMENT_LINK: 'Payment link' },
    cashReceived: 'Cash received (AED)',
    exact: 'Exact',
    change: 'Change to give: {amount}',
    cashShort: 'Must be at least {amount}.',
    linkRef: 'Payment link reference',
    linkHint: "From the customer's payment confirmation. Letters, numbers, - / . # only.",
    prepaid: 'Prepaid: nothing to collect.',
    missing: 'Still needed:',
    missingPieces: 'scan pieces',
    missingPhoto: 'proof photo',
    missingPayment: 'payment',
    swipe: 'Swipe to confirm delivery',
    saving: 'Saving…',
    done: 'Delivered',
    doneLink: 'Delivered – payment link pending',
    giveChange: 'Give change: {amount}',
    nextStop: 'Next stop',
  },
  cash: {
    title: 'My cash',
    inHand: 'Cash in hand',
    today: 'Collected today: {amount}',
    earlier: '{amount} from earlier days is not handed over. Your route cannot start until you hand it to the cashier, or your hub manager allows it.',
    earlierAllowed: `{amount} from earlier days is not handed over. Your hub manager allowed today's route: "{reason}". Hand it over today.`,
    handOver: 'Hand over to the cashier',
    showQr: 'Show this code to the cashier. They scan it and count the cash.',
    validUntil: 'Works once, until {time}.',
    newQr: 'Show a new code',
    copy: 'Copy code',
    copied: 'Copied',
    nothing: 'No cash to hand over.',
    handedOver: 'Handed over: {amount} · receipt {number}',
    leftWithYou: '{amount} is still with you: the cashier counted less. Talk to the cashier.',
    collectedTitle: 'Cash collected today',
    noneCollected: 'No cash collected today.',
    receiptsTitle: 'Receipts',
    noReceipts: 'No receipts yet.',
    receipt: '{number} · {time} · {by}',
    homeLink: 'My cash: {amount} in hand',
    blocked: 'Hand over {amount} from earlier days to the cashier first, or ask your hub manager to allow the route start.',
    openMyCash: 'Open My cash',
    allowedToday: "Your hub manager allowed today's route although {amount} from earlier days is not handed over.",
  },
  shift: {
    off: 'You are off shift',
    onSince: 'On shift since {time}',
    start: 'Start shift',
    end: 'End shift',
    endSure: 'Tap again to end',
    trackingOn: 'Your location is shared with the office every 30 seconds while you are on shift.',
    trackingOff: 'Start your shift when you begin work. Your location is shared only while you are on shift.',
    noGps: 'Location not available: allow location for this app.',
  },
  outbox: {
    waiting: '{count} saved on the phone, waiting for signal',
    waitingOne: '1 action saved on the phone, waiting for signal',
    sendNow: 'Send now',
    refused: 'Not accepted by the office:',
    discard: 'Remove',
    types: { DOOR_SCAN: 'Door scan', PHOTO: 'Photo', DELIVER: 'Delivered', FAIL: 'Not delivered' },
    keptDelivery: 'No signal: saved on the phone. It will be sent by itself when the signal is back. Keep the app open if you can.',
    keptAttempt: 'No signal: saved on the phone. It will be sent by itself when the signal is back, and then the customer gets the WhatsApp message.',
    scanKept: 'No signal: checked on the phone, sent later.',
    photoKept: 'Photo saved on the phone ({kind}); it will be sent when the signal is back.',
    attemptPhotoKept: 'Photo with location saved on the phone; it will be sent when the signal is back.',
    kept: 'Saved on phone',
    signOutBlocked: 'Some actions are still on the phone. Wait for signal so they are sent before you sign out.',
  },
  manual: {
    open: 'Label damaged? Ask for approval',
    hint: 'The label cannot be scanned. Take a photo of the damaged label and one of the parcel. Your team leader or hub manager approves; then the piece counts as scanned.',
    whichPiece: 'Which piece?',
    labelPhoto: 'Photo of the damaged label',
    parcelPhoto: 'Photo of the parcel',
    note: 'What happened to the label? (optional)',
    send: 'Send for approval',
    pending: 'Damaged label: waiting for approval',
    rejected: 'Not approved.',
  },
  fail: {
    reasonTitle: 'Why could you not deliver?',
    proofTitle: 'Proof of attempt',
    calls: 'Calls: {valid} of {required} (at least {minutes} minutes apart)',
    noPhone: 'This parcel has no number. Ask your team leader what to do.',
    photo: 'Photo of the place, with location',
    photoHint: 'Show the door, villa or building. The phone adds where you are; location must be on.',
    photoSaved: 'Photo saved with location, {time}',
    noGps: 'This photo has no location. Turn on location for this app and take it again.',
    whatsappWillSend: 'A WhatsApp message goes to the customer when you save.',
    whatsappOff: 'No WhatsApp message (switched off in Rules).',
    whatsappNoNumber: 'No WhatsApp message: the parcel has no number.',
    note: 'Note (optional)',
    missing: { CALLS: 'calls', PHOTO: 'photo with location' },
    save: 'Save as not delivered',
    saved: 'Saved: not delivered',
    whatsappSent: 'The customer got a WhatsApp message.',
    whatsappNotSent: 'No WhatsApp message was sent.',
    keepParcel: 'Keep the parcel and bring it back to the hub. Your team leader decides the next step.',
  },
  call: {
    call: 'Call customer',
    callAlt: 'Call other number',
    again: 'Call again in {time}',
    rule: 'The app keeps a record of every call. Wait {minutes} minutes between calls.',
    made: 'Calls today: {count} ({times})',
  },
  parcel: {
    back: 'Back',
    pieces: '{count} pieces',
    onePiece: '1 piece',
    cod: 'COD {amount}',
    noCod: 'Prepaid',
    carriedOver: 'From an earlier day',
    customer: 'Customer',
    phone: 'Mobile',
    altPhone: 'Other number',
    callHint: 'Numbers stay hidden. Calling opens once your route has started.',
    address: 'Address',
    district: 'District',
    pin: 'Location pin',
    noPin: 'No pin yet',
    searchAddress: 'Search the address in maps',
    openMap: 'Open in maps',
    details: 'Parcel',
    merchant: 'Merchant',
    weight: 'Weight',
    deliveryType: 'Delivery type',
    timeSlot: 'Time slot',
    description: 'Contents',
    attempts: 'Attempts so far',
    piecesTitle: 'Pieces',
    pieceState: {
      EXPECTED: 'Not in hub',
      IN_HUB: 'In hub',
      IN_VEHICLE: 'In your vehicle',
      AT_DOOR: 'Scanned at the door',
    },
    deliver: 'Deliver',
    notDelivered: 'Not delivered',
    notMine: 'This parcel is no longer on your list.',
  },
  scan: {
    title: 'Scan & load',
    progress: '{loaded} of {total} pieces loaded',
    startCamera: 'Open camera to scan',
    stopCamera: 'Close camera',
    cameraBlocked: 'The camera is blocked. Allow the camera for this app in the phone settings.',
    noCamera: 'No camera found on this device.',
    pdaReady: 'PDA scanner? Just press its scan button.',
    testBox: 'Test scan (local testing only)',
    testGo: 'Scan',
    answer: {
      LOADED: 'Loaded',
      ALREADY_LOADED: 'Already loaded',
      ALREADY_OUT: 'Already out for delivery',
      NOT_YOURS: 'Not on your list. Give it back to the dispatcher.',
      NOT_IN_HUB: 'Not scanned in at the hub. Ask hub staff to scan it in first.',
      SCAN_PIECE_LABELS: 'This parcel has several pieces: scan each piece label (1/3, 2/3…).',
      UNKNOWN: 'Label not known. Check you scanned the Blink or client label.',
    },
    parcelProgress: '{loaded} of {total} pieces of this parcel',
    parcelComplete: 'All pieces of this parcel are loaded.',
    nothing: 'Nothing to load. Parcels given to you appear here.',
    notInHubHint: 'A piece is not scanned in at the hub yet.',
    reportMissing: 'Report missing piece',
    missingNote: 'What happened? (optional)',
    missingSend: 'Send to dispatcher',
    missingReported: 'Reported missing to the dispatcher',
    cancel: 'Cancel',
    startRoute: 'Start route ({count} ready)',
    startAnyway: 'Start anyway ({count} ready)',
    leaveBehind: 'Not fully loaded: {count}. They stay at the hub and the dispatcher sees them as missing at load.',
  },
  status: {
    ASSIGNED: 'To load',
    LOADED: 'Loaded',
    OUT_FOR_DELIVERY: 'On the route',
    IN_TRANSIT: 'Going there now',
    ON_HOLD: 'On hold',
  },
  live: {
    live: 'Live',
    connecting: 'Connecting…',
    offline: 'Offline – will update when back',
  },
  menu: {
    signOut: 'Sign out',
    help: 'Help',
  },
  passwordProblems: {
    TOO_SHORT: 'Too short. Use at least {min} characters.',
    TOO_LONG: 'Too long.',
    TOO_COMMON: 'Too common or easy to guess.',
    CONTAINS_NAME: 'Must not contain your name or email.',
    SAME_AS_CURRENT: 'Must be different from the temporary password.',
  },
  errors: {
    VALIDATION_FAILED: 'Please check what you typed.',
    UNAUTHENTICATED: 'Please sign in.',
    SESSION_EXPIRED: 'You were signed out. Please sign in again.',
    FORBIDDEN: 'You do not have access to this.',
    NOT_FOUND: 'Not found.',
    CONFLICT: 'This was already done.',
    INVALID_CREDENTIALS: 'Username or password is incorrect.',
    ACCOUNT_LOCKED: 'Account temporarily locked.',
    ACCOUNT_DISABLED: 'This account is disabled. Contact your team leader.',
    TOO_MANY_ATTEMPTS: 'Too many attempts. Please wait 15 minutes.',
    PASSWORD_CHANGE_REQUIRED: 'Please choose your own password first.',
    PASSWORD_POLICY: 'This password is not allowed.',
    WRONG_CURRENT_PASSWORD: 'The temporary password is incorrect.',
    INVALID_RESET_TOKEN: 'This link is invalid or expired.',
    INVALID_SCOPE: 'Not allowed.',
    ORIGIN_NOT_ALLOWED: 'Request blocked for security.',
    STATUS_MOVE_NOT_ALLOWED: 'This change is not allowed.',
    REASON_REQUIRED: 'Please give a reason.',
    NOT_A_COURIER: 'This app is for couriers. Staff use the Blink Ops console.',
    USE_DRIVER_APP: 'Please use the driver app.',
    ROUTE_START_BLOCKED: 'The route cannot start yet. Ask your hub manager.',
    NOTHING_LOADED: 'Scan at least one parcel completely before starting the route.',
    CALL_LOCKED: 'Wait 5 minutes between calls to the same customer.',
    NO_PHONE: 'This parcel has no number to call. Ask your team leader.',
    DELIVERY_INCOMPLETE: 'Something is missing: scan every piece, take the photo and enter the payment.',
    BAD_FILE: 'This photo could not be used. Take it again.',
    ATTEMPT_INCOMPLETE: 'Proof is missing: check the list above.',
    MAINTENANCE: 'Blink Ops is being restored. Try again in a few minutes.',
    INVALID_TWO_FACTOR_CODE: 'The code is wrong.',
    TWO_FACTOR_EXPIRED: 'Sign in again.',
    TWO_FACTOR_SETUP_REQUIRED: 'Not allowed.',
    SYSTEM_LOCKED: 'Not allowed.',
    JOB_RUNNING: 'Please wait a moment and try again.',
    INTERNAL: 'Something went wrong. Please try again.',
    NETWORK: 'No connection to the server. Check your signal.',
  },
};

export type Dictionary = typeof en;
